import { usePlanStatus } from "@circle-react/hooks/usePlanStatus";
import { useLocation } from "@tanstack/react-router";
import { useState } from "react";

import { t } from "@/i18n-js/instance";
import { useCurrentPlanApi } from "@/react/components/SettingsApp/Plan/hooks/useCurrentPlanApi";
import { isDowngrading } from "@/react/helpers/planHelpers";
import { useScheduleDowngradesApi } from "@/react/hooks/useScheduleDowngradesApi";

import { UPGRADE_PATHS } from "../constants";
import type { UpgradePlan } from "../types";
import { useUpgradePlanContext } from "../UpgradePlanContext";
import { useUpgradePlanNavigate } from "../upgradePlanRouterHelpers";
import { useAccountPaymentProcessorsApi } from "./useAccountPaymentProcessorsApi";
import { useAccountSubscriptionApi } from "./useAccountSubscriptionApi";

const i18nRoot = "pricing_plans.upgrade_modal";

interface UpgradePlanFormProps {
  conversationUuid?: string;
  showProration: boolean;
  source: string;
  usedForWorkflows: boolean;
}

interface LoadingMessage {
  subtitle: string;
  title: string;
}

export const useUpgradePlanForm = ({
  usedForWorkflows,
  showProration,
  conversationUuid,
  source,
}: UpgradePlanFormProps) => {
  const { couponId, plan } = useUpgradePlanContext();
  const { createCheckoutSession } = useAccountPaymentProcessorsApi();
  const navigate = useUpgradePlanNavigate();
  const { pathname } = useLocation();
  const [loadingMessage, setLoadingMessage] = useState<LoadingMessage>({
    title: "",
    subtitle: "",
  });
  const {
    isCommunityOnTrial,
    currentPlanTier,
    isExpiredTrial,
    isCommunitySubscriptionIncompleteExpired,
  } = usePlanStatus();
  const { scheduleDowngrade } = useScheduleDowngradesApi({
    onScheduleDowngradeSuccess: () => {
      void navigate({ to: UPGRADE_PATHS.SCHEDULED_DOWNGRADE_SUCCESS });
    },
  });
  const { updateSubscription, createEmailHubSubscription } =
    useAccountSubscriptionApi({
      usedForWorkflows,
      source,
    });

  const { currentPlan } = useCurrentPlanApi();

  const emailHubOnlySubmitHandler = (): void => {
    setLoadingMessage({
      title: t(
        "pricing_plans.upgrade_modal.loading.preparing_hub_subscription",
      ),
      subtitle: t("pricing_plans.upgrade_modal.updating_plan_description"),
    });
    void navigate({ to: UPGRADE_PATHS.LOADING });
    createEmailHubSubscription();
  };

  const onSubmitHandler = (): void => {
    const isAtPlanDetails = pathname === UPGRADE_PATHS.PLAN_DETAILS;
    const isAtScheduledDowngradeConfirmation =
      pathname === UPGRADE_PATHS.SCHEDULED_DOWNGRADE_CONFIRMATION;
    const isIntervalDowngradeWithScheduleEligible =
      plan?.eligible_for_schedule_downgrade;

    if (isAtPlanDetails && showProration) {
      void navigate({ to: UPGRADE_PATHS.PRORATION });
    } else if (
      isIntervalDowngradeWithScheduleEligible &&
      !isAtScheduledDowngradeConfirmation
    ) {
      void navigate({ to: UPGRADE_PATHS.SCHEDULED_DOWNGRADE_CONFIRMATION });
    } else {
      handleDowngrade(plan, couponId);
    }
  };

  const handleDowngrade = (plan: UpgradePlan, couponId?: number): void => {
    if (isCommunityOnTrial && !isExpiredTrial) {
      changePlan(plan, couponId);
      return;
    }

    const isDowngradingThePlan =
      currentPlanTier && isDowngrading(currentPlanTier, plan.tier);
    if (isDowngradingThePlan) {
      void navigate({ to: UPGRADE_PATHS.CAN_DOWNGRADE });
    } else {
      changePlan(plan, couponId);
    }
  };

  const changePlan = (plan: UpgradePlan, couponId?: number): void => {
    if (
      isCommunityOnTrial ||
      isExpiredTrial ||
      isCommunitySubscriptionIncompleteExpired
    ) {
      setLoadingMessage({
        subtitle: "",
        title: t([i18nRoot, "stripe_checkout_title"]),
      });
      createCheckoutSession({
        plan_id: plan.id,
        coupon_id: couponId,
        source,
        conversation_uuid: conversationUuid,
      });
    } else {
      setLoadingMessage({
        subtitle: t([i18nRoot, "updating_plan_description"]),
        title: t([i18nRoot, "updating_plan"]),
      });
      if (plan.eligible_for_schedule_downgrade) {
        scheduleDowngrade(plan.id);
      } else {
        updateSubscription({ planId: plan.id, planCouponId: couponId });
      }
    }
    void navigate({ to: UPGRADE_PATHS.LOADING });
  };

  return {
    onSubmitHandler,
    changePlan,
    loadingMessage,
    emailHubOnlySubmitHandler,
    currentPlan,
  };
};
